/**
 * Core Badge — variant-based status/label indicator.
 */
import React from 'react';
import { cn } from '@/lib/utils';

export interface BadgeProps {
  variant?: 'default' | 'success' | 'warning' | 'danger' | 'info' | 'outline';
  size?: 'sm' | 'md';
  children: React.ReactNode;
  className?: string;
  dot?: boolean;
  icon?: React.ReactNode;
}

const variantClasses: Record<string, string> = {
  default: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  success: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400',
  warning: 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400',
  danger:  'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400',
  info:    'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400',
  outline: 'bg-transparent border border-slate-300 text-slate-600 dark:border-slate-600 dark:text-slate-400',
};

const dotColors: Record<string, string> = {
  default: 'bg-slate-500', success: 'bg-emerald-500', warning: 'bg-amber-500',
  danger: 'bg-red-500', info: 'bg-blue-500', outline: 'bg-slate-400',
};

export const Badge: React.FC<BadgeProps> = ({ variant = 'default', size = 'sm', dot, icon, children, className }) => (
  <span className={cn(
    "inline-flex items-center gap-1.5 font-medium rounded-full",
    size === 'sm' ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs',
    variantClasses[variant], className
  )}>
    {dot && <span className={cn("h-1.5 w-1.5 rounded-full", dotColors[variant])} />}
    {icon && <span className="flex items-center">{icon}</span>}
    {children}
  </span>
);
export default Badge;
